// client/src/components/Reservations/components/EditReservationForm.jsx
import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import IsoDatePicker from './IsoDatePicker';
//import { availabilityService } from '../services/availabilityService';

export default function EditReservationForm({ reservation, onSave, onCancel, loading }) {
  const [formData, setFormData] = useState({
    date: reservation.date,
    time: reservation.time,
    party_size: reservation.party_size || 1,
    special_requests: reservation.special_requests || ''
  });
  
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [errors, setErrors] = useState({});

  // Load available time slots when date changes
  useEffect(() => {
    if (formData.date && formData.date !== reservation.date) {
      loadAvailableSlots();
    }
  }, [formData.date]);

  const loadAvailableSlots = async () => {
    if (!formData.date) return;
    
    setLoadingSlots(true);
    try {
      // Get business ID from reservation
      const businessId = reservation.business_id;
      const serviceId = reservation.items?.[0]?.offering_id || reservation.service_id;
      
      // const slots = await availabilityService.getAvailableSlots({
      //   businessId,
      //   serviceId,
      //   date: formData.date,
      //   party_size: formData.party_size,
      //   exclude_reservation_id: reservation.id // Exclude current reservation from conflicts
      // });
      
      setAvailableSlots(slots);
    } catch (err) {
      console.error('Failed to load available slots:', err);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear related errors
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.date) {
      newErrors.date = 'Date is required';
    }
    
    if (!formData.time) {
      newErrors.time = 'Time is required';
    }
    
    if (formData.party_size < 1) {
      newErrors.party_size = 'Party size must be at least 1';
    }
    
    // Check if selected date/time is in the past
    const selectedDateTime = DateTime.fromISO(`${formData.date}T${formData.time}`);
    const now = DateTime.now();
    
    if (selectedDateTime <= now) {
      newErrors.datetime = 'Cannot schedule reservation in the past';
    }
    
    // Check if time slot is available (if slots were loaded)
    if (availableSlots.length > 0 && !availableSlots.some(slot => slot.start === formData.time)) {
      newErrors.time = 'Selected time is not available';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const updates = {
        date: formData.date,
        time: formData.time,
        party_size: formData.party_size,
        special_requests: formData.special_requests
      };
      
      // Only include changed fields
      const changedFields = {};
      Object.keys(updates).forEach(key => {
        if (updates[key] !== reservation[key]) {
          changedFields[key] = updates[key];
        }
      });
      
      if (Object.keys(changedFields).length === 0) {
        onCancel(); // No changes made
        return;
      }
      
      await onSave(changedFields);
    } catch (err) {
      console.error('Failed to update reservation:', err);
      // Error handling is done in parent component
    }
  };

  const originalDateTime = DateTime.fromISO(`${reservation.date}T${reservation.time}`);
  const minDate = DateTime.now().plus({ days: 1 }).toISODate(); // Next day minimum

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900">Edit Reservation</h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Current Details */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">Current Reservation</h4>
          <div className="text-sm text-gray-600 space-y-1">
            <p>Date: {originalDateTime.toFormat('EEEE, MMMM dd, yyyy')}</p>
            <p>Time: {originalDateTime.toFormat('h:mm a')}</p>
            <p>Party Size: {reservation.party_size || 1} {(reservation.party_size || 1) === 1 ? 'guest' : 'guests'}</p>
          </div>
        </div>

        {/* New Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            New Date
          </label>
          <IsoDatePicker
            value={formData.date}
            onChange={(date) => handleInputChange('date', date)}
            minDate={minDate}
            className={`w-full ${errors.date ? 'border-red-500' : ''}`}
            placeholderText="Select new date"
          />
          {errors.date && (
            <p className="mt-1 text-sm text-red-600">{errors.date}</p>
          )}
        </div>

        {/* New Time */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            New Time
          </label>
          {formData.date === reservation.date ? (
            // Same date - show current time as disabled option plus available alternatives
            <select
              value={formData.time}
              onChange={(e) => handleInputChange('time', e.target.value)}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.time ? 'border-red-500' : ''}`}
              disabled={loadingSlots}
            >
              <option value={reservation.time}>
                {DateTime.fromISO(`${reservation.date}T${reservation.time}`).toFormat('h:mm a')} (Current)
              </option>
              {availableSlots.map((slot) => (
                <option key={slot.start} value={slot.start}>
                  {DateTime.fromISO(`${formData.date}T${slot.start}`).toFormat('h:mm a')}
                </option>
              ))}
            </select>
          ) : (
            // Different date - show available slots
            <select
              value={formData.time}
              onChange={(e) => handleInputChange('time', e.target.value)}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.time ? 'border-red-500' : ''}`}
              disabled={loadingSlots || !formData.date}
            >
              <option value="">
                {loadingSlots ? 'Loading available times...' : 'Select time'}
              </option>
              {availableSlots.map((slot) => (
                <option key={slot.start} value={slot.start}>
                  {DateTime.fromISO(`${formData.date}T${slot.start}`).toFormat('h:mm a')}
                </option>
              ))}
            </select>
          )}
          {errors.time && (
            <p className="mt-1 text-sm text-red-600">{errors.time}</p>
          )}
          {formData.date !== reservation.date && availableSlots.length === 0 && !loadingSlots && formData.date && (
            <p className="mt-1 text-sm text-yellow-600">No available time slots for this date</p>
          )}
        </div>

        {/* Party Size */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Party Size
          </label>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={() => handleInputChange('party_size', Math.max(1, formData.party_size - 1))}
              className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
              disabled={formData.party_size <= 1}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            </button>
            <input
              type="number"
              min="1"
              value={formData.party_size}
              onChange={(e) => handleInputChange('party_size', parseInt(e.target.value) || 1)}
              className={`w-20 px-3 py-2 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.party_size ? 'border-red-500' : ''}`}
            />
            <button
              type="button"
              onClick={() => handleInputChange('party_size', formData.party_size + 1)}
              className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </button>
          </div>
          {errors.party_size && (
            <p className="mt-1 text-sm text-red-600">{errors.party_size}</p>
          )}
        </div>

        {/* Special Requests */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Special Requests
          </label>
          <textarea
            value={formData.special_requests}
            onChange={(e) => handleInputChange('special_requests', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Any special requests or notes..."
          />
        </div>

        {/* Validation Errors */}
        {errors.datetime && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-600">{errors.datetime}</p>
          </div>
        )}

        {/* Change Summary */}
        {(formData.date !== reservation.date || 
          formData.time !== reservation.time || 
          formData.party_size !== reservation.party_size ||
          formData.special_requests !== (reservation.special_requests || '')) && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Changes Summary:</h4>
            <div className="text-sm text-blue-800 space-y-1">
              {formData.date !== reservation.date && (
                <p>• Date: {originalDateTime.toFormat('MMM dd')} → {DateTime.fromISO(formData.date).toFormat('MMM dd, yyyy')}</p>
              )}
              {formData.time !== reservation.time && (
                <p>• Time: {originalDateTime.toFormat('h:mm a')} → {DateTime.fromISO(`${formData.date}T${formData.time}`).toFormat('h:mm a')}</p>
              )}
              {formData.party_size !== reservation.party_size && (
                <p>• Party Size: {reservation.party_size} → {formData.party_size}</p>
              )}
              {formData.special_requests !== (reservation.special_requests || '') && (
                <p>• Special Requests: Updated</p>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3 pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={loading || loadingSlots}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Saving Changes...' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}